import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Loader2, Search, Users } from "lucide-react";
import { adminSearchClients, adminGetClientTimeline } from "@/lib/api/admin-clients.functions";
import { MATTER_CATEGORIES } from "@/lib/email-presets";
import { portalLabel } from "@/lib/client-portals";

export const Route = createFileRoute("/_authenticated/portal/admin/clients")({
  head: () => ({
    meta: [
      { title: "Client directory · NDLCS Admin" },
      {
        name: "description",
        content:
          "Search every client by name, email, phone, company, matter number, invoice, service, date or ATS score.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminClients,
});

const rand = (c: number) => `R${(c / 100).toFixed(2)}`;

function AdminClients() {
  const search = useServerFn(adminSearchClients);
  const timeline = useServerFn(adminGetClientTimeline);

  const [q, setQ] = useState("");
  const [service, setService] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [minAts, setMinAts] = useState("");
  const [openEmail, setOpenEmail] = useState<string | null>(null);

  const list = useMutation({
    mutationFn: () =>
      search({
        data: {
          q: q || undefined,
          service: service || undefined,
          from: from || undefined,
          to: to || undefined,
          minAts: minAts ? Number(minAts) : undefined,
        },
      }),
  });

  const detail = useMutation({
    mutationFn: (email: string) => timeline({ data: { email } }),
  });

  useEffect(() => {
    list.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5 py-4">
      <header>
        <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold">
          <Users className="h-3.5 w-3.5" /> Admin
        </p>
        <h1 className="font-display text-2xl text-navy">Client directory</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Search by name, email, phone, company, matter number, invoice, service, date or ATS score.
        </p>
      </header>

      <form
        className="grid gap-2 rounded-lg border border-border bg-card p-3 sm:grid-cols-6"
        onSubmit={(e) => { e.preventDefault(); list.mutate(); }}
      >
        <div className="sm:col-span-2">
          <label htmlFor="cd-q" className="text-[11px] font-semibold uppercase text-muted-foreground">Search</label>
          <input id="cd-q" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email, phone, matter, invoice"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label htmlFor="cd-service" className="text-[11px] font-semibold uppercase text-muted-foreground">Service</label>
          <select id="cd-service" value={service} onChange={(e) => setService(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
            <option value="">All</option>
            {MATTER_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cd-from" className="text-[11px] font-semibold uppercase text-muted-foreground">From</label>
          <input id="cd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label htmlFor="cd-to" className="text-[11px] font-semibold uppercase text-muted-foreground">To</label>
          <input id="cd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label htmlFor="cd-ats" className="text-[11px] font-semibold uppercase text-muted-foreground">Min ATS</label>
          <input id="cd-ats" type="number" min={0} max={100} value={minAts}
            onChange={(e) => setMinAts(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm" />
        </div>
        <div className="sm:col-span-6">
          <button type="submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-sm font-semibold text-primary-foreground">
            {list.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>
      </form>

      {list.data && (
        <p className="text-xs text-muted-foreground">
          {list.data.total} client{list.data.total === 1 ? "" : "s"} matched.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="p-2">Client</th>
              <th className="p-2">Portal</th>
              <th className="p-2">Matters</th>
              <th className="p-2">Invoices</th>
              <th className="p-2">ATS</th>
              <th className="p-2">Since</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {(list.data?.clients ?? []).map((c: any) => (
              <tr key={c.email} className="border-t border-border align-top">
                <td className="p-2">
                  <p className="font-medium text-navy">{c.name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{c.email}</p>
                  {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                </td>
                <td className="p-2 text-xs">{portalLabel(c.portal_type)}</td>
                <td className="p-2 text-xs">
                  {c.matters.length === 0 ? "—" : c.matters.slice(0, 3).map((m: any) => (
                    <div key={m.id}>{m.matter_number ?? m.title}</div>
                  ))}
                </td>
                <td className="p-2 text-xs">
                  {c.invoices.length === 0 ? "—" : c.invoices.slice(0, 3).map((i: any) => (
                    <div key={i.id}>{i.reference} · {rand(i.total_cents)} · {i.status}</div>
                  ))}
                </td>
                <td className="p-2 text-xs">{c.bestAtsScore ?? "—"}</td>
                <td className="p-2 text-xs">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-2 text-right">
                  <button
                    className="text-xs font-semibold text-navy hover:underline"
                    onClick={() => {
                      setOpenEmail(c.email);
                      detail.mutate(c.email);
                    }}
                  >
                    Timeline
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openEmail && (
        <section className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg text-navy">Timeline — {openEmail}</h2>
            <button className="text-xs text-muted-foreground hover:underline" onClick={() => setOpenEmail(null)}>
              Close
            </button>
          </div>
          {detail.isPending && (
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {detail.data && (
            <>
              <ol className="mt-3 space-y-2 border-l-2 border-border pl-4">
                {detail.data.events.map((e: any) => (
                  <li key={e.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-gold" />
                    <p className="text-sm font-medium text-navy">{e.title}</p>
                    {e.detail && <p className="text-xs text-muted-foreground">{e.detail}</p>}
                    <p className="text-[11px] text-muted-foreground">
                      {new Date(e.created_at).toLocaleString()}
                    </p>
                  </li>
                ))}
                {detail.data.events.length === 0 && (
                  <li className="text-sm text-muted-foreground">No events recorded yet.</li>
                )}
              </ol>
              {detail.data.documents.length > 0 && (
                <div className="mt-3 border-t border-border pt-2">
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">Documents on file</p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {detail.data.documents.map((d: any) => (
                      <li key={d.id} className="flex items-center justify-between gap-2">
                        <span className="truncate">{d.name}</span>
                        {d.url && (
                          <a href={d.url} target="_blank" rel="noreferrer"
                            className="text-xs font-semibold text-navy hover:underline">Open</a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
